<template>
  <div>
    <v-btn v-if="!hideButton" :elevation="0" @click="open()">
      {{ $t("create") }}
    </v-btn>

    <confirm-action-dialog
      v-if="isDialogActive"
      width="1050"
      :persistent="true"
      :supressKeyboardActions="true"
      :title="$t('template.createTemplate')"
      :isDialogActive.sync="isDialogActive"
      :rightText="$t('create')"
      :loading="isLoading"
      :fullscreen="fullscreen"
      :rightLoading="isLoading"
      :rightDisabled="!isFormValid || !template.content.body || !template.content.subject"
      @leftClick="close"
      @close="close"
      @rightClick="onRightClick"
    >
      <v-form v-model="isFormValid">
        <v-row class="pt-3">
          <v-col cols="12" md="12">
            <debug>
              <v-text-field v-model="template.key" :label="$t('key')" :rules="rules()" persistent-hint outlined />
            </debug>
            <v-text-field
              v-model="template.meta.title"
              :label="$t('objects.template.meta.title')"
              :hint="$t('objects.template.meta.titleHint')"
              :rules="rules(RulesEnum.REQUIRED_RULE)"
              ref="startField"
              persistent-hint
              outlined
            />
            <v-autocomplete
              v-model="template.meta.language"
              :label="$t('objects.template.meta.language')"
              :hint="$t('objects.template.meta.languageHint')"
              outlined
              :items="availableLanguages"
              :rules="rules()"
              :filter="getAutocompleteFilter((e) => [$t(`enums.LanguageCodeEnum.${e}`)])"
            >
              <template #item="{ item }">
                {{ item | getFlagEmojiByLanguage }} {{ $t(`enums.LanguageCodeEnum.${item}`) }}
              </template>
              <template #selection="{ item }">
                {{ item | getFlagEmojiByLanguage }} {{ $t(`enums.LanguageCodeEnum.${item}`) }}
              </template>
            </v-autocomplete>

            <custom-field-boolean
              v-if="!partnerId"
              v-model="template.isPublic"
              :customField="{
                label: $t('objects.template.isPublic'),
                hint: $t('objects.template.isPublicHint')
              }"
            />

            <v-select
              :value="isPlain"
              :label="$t('objects.template.meta.contentFormat')"
              :hint="$t('objects.template.meta.contentFormatHint')"
              persistent-hint
              outlined
              :items="[true, false]"
              @change="isPlain = $event"
            >
              <template #item="{ item }">
                <span v-if="item">
                  {{ $t("enums.MessageContentFormatEnum.plain") }}
                </span>
                <span v-else>
                  {{ $t("enums.MessageContentFormatEnum.html") }}
                </span>
              </template>
              <template #selection="{ item }">
                <span v-if="item">
                  {{ $t("enums.MessageContentFormatEnum.plain") }}
                </span>
                <span v-else>
                  {{ $t("enums.MessageContentFormatEnum.html") }}
                </span>
              </template>
            </v-select>
            <template-editor-subject
              :partnerId="partnerId"
              :subject.sync="template.content.subject"
              :addPlaceholderInSubject="true"
              :rules="rules(RulesEnum.REQUIRED_RULE)"
            />

            <v-textarea
              v-if="isPlain"
              v-model="template.content.body"
              outlined
              auto-grow
              :label="$t('components.template.detail.body.body')"
            >
              <template #append>
                <template-editor-add-placeholder
                  @addPlaceholder="template.content.body += $event"
                  :partnerId="partnerId"
                  class="mt-n2"
                />
              </template>
            </v-textarea>
            <template-editor
              v-else
              v-model="template.content.body"
              :key="'template-editor' + isPlain"
              :isPlain="isPlain"
              :readOnly="false"
              :editConfiguration="editConfiguration"
              :partnerId="partnerId"
              :label="$t('components.template.detail.body.body')"
            />
          </v-col>
        </v-row>
        <debug>
          {{ template }}
        </debug>
      </v-form>
    </confirm-action-dialog>
  </div>
</template>

<script lang="ts">
import LatestEntriesCardEmpty from "@/components/cards/LatestEntriesCardEmpty.vue";
import CustomFieldListForm from "@/components/report/CustomFieldListForm.vue";
import TemplateEditor, { TemplateEditConfiguration } from "@/components/template/TemplateEditor.vue";
import Debug from "@/components/utility/Debug.vue";
import PaginatedTable from "@/components/utility/v2/PaginatedTable.vue";
import { LanguageCodeEnum } from "@/lib/enum/language-code.enum";
import { MessageContentFormatEnum } from "@/lib/enum/templateEnums/messageContentFormat.enum";
import { TemplateEditorEnum } from "@/lib/enum/templateEnums/tempalteEditor.enum";
import { IDialog } from "@/lib/interfaces/dialog.interface";
import { simpleDoubleDigitDate } from "@/lib/utility/date-helper";
import { getAutocompleteFilter } from "@/lib/utility/filter";
import { handleError } from "@/lib/utility/handleError";
import PartnerFallbackMixin from "@/mixins/PartnerFallbackMixin.vue";
import RulesMixin from "@/mixins/RulesMixin.vue";
import { IReference } from "@/models/reference.entity";
import { buildTemplate, ITemplate } from "@/models/template.entity";
import { TemplateMeta } from "@/models/template-meta.entity";
import { ConfigModule } from "@/store/modules/config";
import { mixins } from "vue-class-component";
import { Component, Prop } from "vue-property-decorator";
import CustomFieldBoolean from "../report/CustomFieldBoolean.vue";
import ConfirmActionDialog from "../utility/ConfirmActionDialog.vue";
import TemplateEditorAddPlaceholder from "./TemplateEditorAddPlaceholder.vue";
import TemplateEditorSubject from "./TemplateEditorSubject.vue";
import { PartnerModule } from "@/store/modules/partner";
import { getFlagEmojiByLanguage } from "@/lib/CountryCodeHelper";

@Component({
  components: {
    PaginatedTable,
    ConfirmActionDialog,
    CustomFieldListForm,
    LatestEntriesCardEmpty,
    Debug,
    CustomFieldBoolean,
    TemplateEditorSubject,
    TemplateEditor,
    TemplateEditorAddPlaceholder
  },
  filters: {
    simpleDoubleDigitDate,
    getFlagEmojiByLanguage
  }
})
export default class TemplateCreateDialog extends mixins(PartnerFallbackMixin, RulesMixin) implements IDialog {
  readonly getAutocompleteFilter = getAutocompleteFilter;

  @Prop()
  partnerId!: string;

  @Prop()
  hideButton!: boolean;

  @Prop({ default: false })
  fullscreen?: boolean;

  @Prop({ default: () => [] })
  refs!: IReference[];

  toggle_exclusive = "";

  isDialogActive = false;

  isLoading = false;

  isFormValid = false;

  template: ITemplate = buildTemplate({
    partnerId: this.partnerId,
    meta: new TemplateMeta({
      title: "",
      editor: TemplateEditorEnum.TIPTAP,
      contentFormat: MessageContentFormatEnum.HTML,
      categories: [],
      language: LanguageCodeEnum.DE
    })
  });

  isLoadingCustomFieldValues = false;

  isPlain = false;

  get editConfiguration() {
    return new TemplateEditConfiguration(this.partnerId, true);
  }

  get availableLanguages() {
    return ConfigModule.availableLanguages;
  }

  /**
   * Opens the create dialog. Optionally preset the template key (e.g. when
   * creating a template for a known but not yet configured template key).
   */
  open(key?: string) {
    this.template = buildTemplate({
      partnerId: this.partnerId,
      key: typeof key === "string" ? key : undefined,
      meta: new TemplateMeta({
        title: "",
        editor: TemplateEditorEnum.TIPTAP,
        contentFormat: MessageContentFormatEnum.HTML,
        categories: [],
        language: PartnerModule.partner.language || LanguageCodeEnum.DE
      })
    });
    this.isPlain =
      this.template.meta?.editor === TemplateEditorEnum.DEFAULT &&
      this.template.meta?.contentFormat === MessageContentFormatEnum.PLAIN;

    this.isDialogActive = true;
    this.$nextTick(() => {
      (this.$refs.startField as HTMLInputElement)?.focus();
    });
  }

  close() {
    this.isDialogActive = false;
  }

  async onRightClick() {
    try {
      this.isLoading = true;
      this.isDialogActive = true;

      if (this.isPlain) {
        this.template.meta.contentFormat = MessageContentFormatEnum.PLAIN;
        this.template.meta.editor = TemplateEditorEnum.DEFAULT;
      } else {
        this.template.meta.contentFormat = MessageContentFormatEnum.HTML;
        this.template.meta.editor = TemplateEditorEnum.TIPTAP;
      }

      await this.template.create();
      this.$emit("created", this.template);
      this.close();
    } catch (e) {
      handleError(e);
    } finally {
      this.isLoading = false;
    }
  }
}
</script>
